import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '@/core/contexts/AuthContext';
import { usePreferences } from '@/core/contexts/PreferencesContext';
import { getCurrentClientId } from '@/core/identity';
import { api } from '@/core/api/apiClient';
import { track } from '@/core/tracking/tracking';
import type { CartContextValue, CartOutput, CheckoutResult } from '../types';

// ════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ════════════════════════════════════════════════════════════════════════════

const SESSION_ID_KEY = 'storefront_session_id';

/**
 * Get or create session ID for anonymous users
 */
function getOrCreateSessionId(): string {
  const existingId = localStorage.getItem(SESSION_ID_KEY);
  if (existingId) {
    return existingId;
  }
  const newId = uuidv4();
  localStorage.setItem(SESSION_ID_KEY, newId);
  return newId;
}

// ════════════════════════════════════════════════════════════════════════════
// CONTEXT
// ════════════════════════════════════════════════════════════════════════════

const CartContext = createContext<CartContextValue | undefined>(undefined);

export function CartProvider({ children }: { children: React.ReactNode }) {
  // Auth state - to detect login/logout for cart merge
  const { user, isLoading: authLoading } = useAuth();
  // Initialize with undefined to distinguish "not yet initialized" from "no user"
  // This prevents false "justLoggedIn" detection on page load when user is already authenticated
  const previousUserIdRef = useRef<string | null | undefined>(undefined);
  const hasInitializedRef = useRef(false);
  
  // Get store slug from PreferencesContext (central source of truth)
  const { storeSlug } = usePreferences();
  
  const [cart, setCart] = useState<CartOutput | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [lastAddedVariantId, setLastAddedVariantId] = useState<string | null>(null);
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  // Drawer controls
  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  // Get session ID for anonymous identification
  const sessionId = getOrCreateSessionId();

  // Get cart API path (apiClient already has the base URL configured)
  const getCartPath = useCallback((path: string = '') => {
    if (!storeSlug) return '';
    return `/storefront/${storeSlug}/cart${path}`;
  }, [storeSlug]);

  // Common headers for cart operations (apiClient handles Authorization automatically)
  const getCartHeaders = useCallback(() => ({
    'X-Session-Id': sessionId,
  }), [sessionId]);

  // ══════════════════════════════════════════════════════════════════════════
  // FETCH CART
  // ══════════════════════════════════════════════════════════════════════════

  const refreshCart = useCallback(async () => {
    if (!storeSlug) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get<CartOutput>(getCartPath(), {
        headers: getCartHeaders(),
      });
      setCart(response.data.data ?? null);
    } catch (err: any) {
      setError(err?.error?.message || 'Failed to fetch cart');
    } finally {
      setLoading(false);
    }
  }, [storeSlug, getCartPath, getCartHeaders]);

  // Fetch cart on mount and when store changes
  // Wait for auth to finish loading to avoid double fetch
  useEffect(() => {
    if (storeSlug && !authLoading) {
      refreshCart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refreshCart is stable via useCallback
  }, [storeSlug, authLoading]);

  // ══════════════════════════════════════════════════════════════════════════
  // MERGE CART ON LOGIN
  // ══════════════════════════════════════════════════════════════════════════

  /**
   * When user logs in, merge their anonymous cart with their user cart.
   * The anonymous cart is transferred to the user (sessionId is cleared on the cart).
   */
  useEffect(() => {
    // Wait for auth to finish loading before detecting login/logout
    if (authLoading) return;
    
    const currentUserId = user?.id || null;
    const previousUserId = previousUserIdRef.current;
    
    // First time initialization - just set the ref, don't trigger merge
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      previousUserIdRef.current = currentUserId;
      return;
    }
    
    // Detect login: went from no user to having a user
    const justLoggedIn = previousUserId === null && currentUserId !== null;
    // Detect logout: went from having a user to no user
    const justLoggedOut = previousUserId !== null && currentUserId === null;
    
    if (justLoggedOut && storeSlug) {
      // User logged out - refresh to get anonymous cart (if any exists for this sessionId)
      console.log('[Cart] User logged out, refreshing cart...');
      refreshCart();
    }
    
    if (justLoggedIn && storeSlug) {
      // Call merge endpoint to transfer anonymous cart to user
      const mergeCart = async () => {
        try {
          console.log('[Cart] User logged in, merging anonymous cart...');
          // Get clientId from anonymous profile for cart attribute linking
          const clientId = getCurrentClientId();
          
          const response = await api.post<CartOutput>(getCartPath('/merge'), { 
            sessionId,
            clientId: clientId || undefined,
          }, {
            headers: getCartHeaders(),
          });
          
          setCart(response.data.data ?? null);
          console.log('[Cart] Cart merged successfully');
        } catch (err) {
          console.error('[Cart] Error merging cart:', err);
          // On error, just refresh to get the user's cart
          await refreshCart();
        }
      };
      
      mergeCart();
    }
    
    // Update ref for next comparison
    previousUserIdRef.current = currentUserId;
  }, [user?.id, authLoading, storeSlug, getCartPath, getCartHeaders, sessionId, refreshCart]);

  // ══════════════════════════════════════════════════════════════════════════
  // ADD TO CART
  // ══════════════════════════════════════════════════════════════════════════

  const addToCart = useCallback(async (variantId: string, quantity: number) => {
    if (!storeSlug) return;

    setIsAdding(true);
    setError(null);

    try {
      const response = await api.post<CartOutput>(getCartPath('/lines'), 
        { items: [{ variantId, quantity }] },
        { headers: getCartHeaders() }
      );
      
      const cartData = response.data.data ?? null;
      setCart(cartData);
      
      // Check if the product was actually added by Shopify
      // Shopify returns quantity: 0 when product is out of stock
      if (cartData) {
        const addedLine = cartData.lines.find(line => line.variantId === variantId);
        if (addedLine && addedLine.quantity === 0) {
          // Shopify rejected the product - it's out of stock
          const errorMessage = addedLine.availableForSale === false 
            ? 'Este producto no está disponible actualmente'
            : 'No hay stock disponible para este producto';
          setError(errorMessage);
          throw new Error(errorMessage);
        }
      }
    } catch (err: any) {
      const errorMessage = err?.message || err?.error?.message || 'Failed to add to cart';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsAdding(false);
    }
  }, [storeSlug, getCartPath, getCartHeaders]);

  // ══════════════════════════════════════════════════════════════════════════
  // UPDATE QUANTITY
  // ══════════════════════════════════════════════════════════════════════════

  const updateQuantity = useCallback(async (lineId: string, quantity: number) => {
    if (!storeSlug) return;

    setIsUpdating(true);
    setError(null);

    try {
      // URL-encode the lineId as it contains slashes (gid://shopify/CartLine/xxx)
      const response = await api.put<CartOutput>(
        getCartPath(`/lines/${encodeURIComponent(lineId)}`),
        { quantity },
        { headers: getCartHeaders() }
      );
      setCart(response.data.data ?? null);
    } catch (err: any) {
      const errorMessage = err?.error?.message || 'Failed to update cart';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  }, [storeSlug, getCartPath, getCartHeaders]);

  // ══════════════════════════════════════════════════════════════════════════
  // REMOVE ITEM
  // ══════════════════════════════════════════════════════════════════════════

  const removeItem = useCallback(async (lineId: string) => {
    if (!storeSlug) return;

    setIsUpdating(true);
    setError(null);

    try {
      // URL-encode the lineId as it contains slashes (gid://shopify/CartLine/xxx)
      const response = await api.delete<CartOutput>(
        getCartPath(`/lines/${encodeURIComponent(lineId)}`),
        { headers: getCartHeaders() }
      );
      setCart(response.data.data ?? null);
    } catch (err: any) {
      const errorMessage = err?.error?.message || 'Failed to remove from cart';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  }, [storeSlug, getCartPath, getCartHeaders]);

  // ══════════════════════════════════════════════════════════════════════════
  // CLEAR CART
  // ══════════════════════════════════════════════════════════════════════════

  const clearCart = useCallback(async () => {
    if (!storeSlug) return;

    setLoading(true);
    setError(null);

    try {
      await api.delete(getCartPath(), { headers: getCartHeaders() });
      // Refresh to get empty cart state
      await refreshCart();
    } catch (err: any) {
      const errorMessage = err?.error?.message || 'Failed to clear cart';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [storeSlug, getCartPath, getCartHeaders, refreshCart]);

  // ══════════════════════════════════════════════════════════════════════════
  // DISCOUNTS
  // ══════════════════════════════════════════════════════════════════════════

  const applyDiscount = useCallback(async (code: string) => {
    if (!storeSlug) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.post<CartOutput>(
        getCartPath('/discounts'),
        { code },
        { headers: getCartHeaders() }
      );
      setCart(response.data.data ?? null);
    } catch (err: any) {
      const errorMessage = err?.error?.message || 'Failed to apply discount';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [storeSlug, getCartPath, getCartHeaders]);

  const removeDiscount = useCallback(async (code: string) => {
    if (!storeSlug) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.delete<CartOutput>(
        getCartPath(`/discounts/${encodeURIComponent(code)}`),
        { headers: getCartHeaders() }
      );
      setCart(response.data.data ?? null);
    } catch (err: any) {
      const errorMessage = err?.error?.message || 'Failed to remove discount';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [storeSlug, getCartPath, getCartHeaders]);

  // ══════════════════════════════════════════════════════════════════════════
  // CHECKOUT
  // ══════════════════════════════════════════════════════════════════════════

  const prepareCheckout = useCallback(async (): Promise<CheckoutResult> => {
    if (!storeSlug) {
      throw new Error('No store selected');
    }

    setLoading(true);
    setError(null);

    try {
      // For anonymous users, pass clientId for cart attribute linking
      // For authenticated users, the backend will use req.user.id
      const clientId = !user ? getCurrentClientId() : null;

      const response = await api.post<CheckoutResult>(
        getCartPath('/checkout'),
        { clientId: clientId || undefined },
        { headers: getCartHeaders() }
      );
      
      const checkoutResult = response.data.data;
      
      // Refresh cart if adjustments were made
      if (checkoutResult?.wasAdjusted) {
        await refreshCart();
      }

      return checkoutResult as CheckoutResult;
    } catch (err: any) {
      const errorMessage = err?.error?.message || 'Failed to prepare checkout';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [storeSlug, getCartPath, getCartHeaders, refreshCart, user]);

  /**
   * Complete checkout flow: track event, prepare checkout (set buyer identity), redirect
   * Use this instead of manually calling prepareCheckout + redirect
   */
  const goToCheckout = useCallback(async (): Promise<void> => {
    if (!cart || cart.lines.length === 0) {
      throw new Error('Cart is empty');
    }

    setIsCheckingOut(true);
    setError(null);

    try {
      // 1. Track InitiateCheckout event
      const contentIds = cart.lines.map((line) => line.productId);
      const contents = cart.lines.map((line) => ({
        id: line.productId,
        variant_id: line.variantId,
        quantity: line.quantity,
        item_price: parseFloat(line.unitPrice.amount || '0'),
      }));
      const numItems = cart.lines.reduce((sum, line) => sum + line.quantity, 0);

      track('InitiateCheckout', {
        content_type: 'product',
        content_ids: contentIds,
        contents,
        value: parseFloat(cart.total.amount || '0'),
        currency: cart.total.currencyCode || 'USD',
        num_items: numItems,
      });

      // 2. Prepare checkout (sets buyer identity in Shopify)
      const result = await prepareCheckout();

      // 3. Redirect to Shopify checkout
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      }
    } catch (err: any) {
      const errorMessage = err?.message || 'Checkout failed';
      setError(errorMessage);
      throw err;
    } finally {
      setIsCheckingOut(false);
    }
  }, [cart, prepareCheckout]);

  // ══════════════════════════════════════════════════════════════════════════
  // CONTEXT VALUE (memoized to prevent unnecessary re-renders)
  // ══════════════════════════════════════════════════════════════════════════

  const value = useMemo<CartContextValue>(() => ({
    // Data
    cart,
    loading,
    error,
    isEmpty: !cart?.lines?.length,
    totalQuantity: cart?.totalQuantity ?? 0,
    storeSlug,
    
    // Drawer state
    isDrawerOpen,
    openDrawer,
    closeDrawer,
    
    // Highlight state for newly added items
    lastAddedVariantId,
    setLastAddedVariantId,
    
    // Actions
    addToCart,
    updateQuantity,
    removeItem,
    clearCart,
    applyDiscount,
    removeDiscount,
    prepareCheckout,
    goToCheckout,
    refreshCart,
    
    // Loading states
    isAdding,
    isUpdating,
    isCheckingOut,
  }), [
    cart,
    loading,
    error,
    storeSlug,
    isDrawerOpen,
    openDrawer,
    closeDrawer,
    lastAddedVariantId,
    addToCart,
    updateQuantity,
    removeItem,
    clearCart,
    applyDiscount,
    removeDiscount,
    prepareCheckout,
    goToCheckout,
    refreshCart,
    isAdding,
    isUpdating,
    isCheckingOut,
  ]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}

/**
 * Get the session ID for cart merge on login
 */
export function getCartSessionId(): string {
  return getOrCreateSessionId();
}
